import type { AssetId } from 'owa-timestream-types';
import {
    closeCurrentTimeStreamProject,
    getAsset,
    getSelectedProjectId,
    moveTimeStreamAsset,
    snapToGrid,
    updateAssetParent,
    removeBucketChild,
} from 'owa-timestream-store';
import { calculateAssetOrder, dropAssetIntoBucket } from 'owa-timestream-asset-sdk-bucket';
import { mutator, orchestrator } from 'satcheljs';
import {
    finishArrowing,
    moveAssetArrow,
    pointerMove,
    pointerUp,
    startDraggingAssets,
} from '../actions';
import getStore from '../store';

const KEYDOWN_MOVE_TIMEOUT = 500;
let timeoutId;

function resetAssetOffset() {
    getStore().assetOffset = null;
}

function moveAsset(dx: number, dy: number) {
    const { assetOffset, isLocked } = getStore();
    if (assetOffset && !isLocked) {
        assetOffset.dx = dx;
        assetOffset.dy = dy;
    }
}

function finishMovingAsset(bucketId?: AssetId, targetMinOrder?: number, targetMaxOrder?: number) {
    const { assetOffset, zoom, hasDraggedFarEnough, selectedAssets, isLocked } = getStore();
    if (assetOffset && hasDraggedFarEnough && selectedAssets.length) {
        const { dx, dy } = assetOffset;
        if ((dx || dy) && !isLocked) {
            const projectId = getSelectedProjectId();
            const moveEndedInBucketDropZone =
                bucketId && targetMinOrder != undefined && targetMaxOrder != undefined;

            if (moveEndedInBucketDropZone) {
                const order = calculateAssetOrder(targetMinOrder, targetMaxOrder);
                dropAssetIntoBucket(projectId, bucketId!, order);
            } else {
                selectedAssets.forEach(assetId => {
                    const { parentId, bounds } = getAsset(projectId, assetId);
                    const docDx = dx / zoom;
                    const docDy = dy / zoom;
                    if (parentId) {
                        removeBucketChild(projectId, assetId, false);
                        updateAssetParent(
                            projectId,
                            assetId,
                            null,
                            undefined,
                            false,
                            snapToGrid(bounds.x + docDx),
                            snapToGrid(bounds.y + docDy)
                        );
                    } else {
                        moveTimeStreamAsset(projectId, assetId, docDx, docDy);
                    }
                });
            }
        }
    }
}

mutator(startDraggingAssets, () => {
    getStore().assetOffset = { dx: 0, dy: 0 };
});

mutator(pointerMove, ({ dx, dy }) => {
    moveAsset(dx, dy);
});

mutator(moveAssetArrow, ({ dx, dy }) => {
    const { assetOffset } = getStore();
    if (!assetOffset) {
        getStore().assetOffset = { dx: 0, dy: 0 };
    }

    // arrowing accumulates the offsets
    moveAsset((assetOffset?.dx ?? 0) + dx, (assetOffset?.dy ?? 0) + dy);
});

orchestrator(moveAssetArrow, () => {
    if (timeoutId != null) {
        clearTimeout(timeoutId);
    }
    timeoutId = setTimeout(() => {
        finishMovingAsset();
        finishArrowing();
    }, KEYDOWN_MOVE_TIMEOUT);
});

orchestrator(pointerUp, ({ bucketId, targetMinOrder, targetMaxOrder }) => {
    finishMovingAsset(bucketId, targetMinOrder, targetMaxOrder);
});

mutator(pointerUp, resetAssetOffset);
mutator(finishArrowing, resetAssetOffset);
mutator(closeCurrentTimeStreamProject, resetAssetOffset);
